import { stringifyGraph } from '@/clients/http'
import { PosReportModel, type CreditRepayment } from '@/http/models/PosReport'
import { useBusiness } from '@/contexts/BusinessContext'

import { PageHeader } from '@/components/atoms'
import { formatDateTime } from '@/components/features/pos/format'
import { PosListCard } from '@/components/features/pos/PosListCard'
import { usePagedReport } from '@/components/features/pos/usePagedReport'

const GRAPH = stringifyGraph({
  '*': true,
  customer: { name: true, phone: true },
  terminal: { name: true, branch: { name: true } },
})

const label = (method?: string) => (method ? method.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '—')

/** Credit customers paid back at a till, every branch (checklist C25; `GET /pos-report/credit-repay`). */
const CreditRepayments = () => {
  const { formatCurrency } = useBusiness()
  const list = usePagedReport<CreditRepayment>(PosReportModel.getCreditRepayments, { graph_enc: GRAPH })

  return (
    <div className="space-y-4">
      <PageHeader title="Credit repayments" description="Payments customers made against their credit balance, newest first." />
      <PosListCard<CreditRepayment>
        {...list}
        onPageChange={list.setPage}
        empty="No credit repayments yet."
        columns={[
          { label: 'Date', render: (r) => formatDateTime(r.created_at) },
          { label: 'Receipt', render: (r) => r.receipt_no || '—', className: 'hidden md:table-cell' },
          {
            label: 'Customer',
            render: (r) => (
              <div>
                <div className="font-medium text-ink">{r.customer?.name ?? '—'}</div>
                {r.customer?.phone && <div className="text-xs text-muted-foreground">{r.customer.phone}</div>}
              </div>
            ),
          },
          { label: 'Branch', render: (r) => r.terminal?.branch?.name ?? '—', className: 'hidden md:table-cell' },
          { label: 'Terminal', render: (r) => r.terminal?.name ?? '—', className: 'hidden lg:table-cell' },
          { label: 'Method', render: (r) => label(r.payment_method) },
          { label: 'Amount', render: (r) => <span className="tabular-nums">{formatCurrency(r.amount_received - (r.amount_returned ?? 0))}</span>, className: 'text-right' },
        ]}
      />
    </div>
  )
}

export default CreditRepayments
